import type { ReactNode } from 'react';

import { cn } from '@nucleus-commerce/ui/lib/utils';

/** One labelled block of a variant's View more. */
export function VariantDetailGroup({
  title,
  action,
  className,
  children,
}: {
  title: ReactNode;
  /** Right of the title: a link, a button. */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        <h4 className="flex-1 text-[13px] font-semibold">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

export function VariantDetailGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 items-start gap-x-2.5 gap-y-2">{children}</div>;
}

/** A label over its input, in a detail group. The input names itself for assistive tech. */
export function VariantDetailField({
  label,
  className,
  children,
}: {
  label: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1 text-xs font-medium', className)}>
      <span>{label}</span>
      {children}
    </div>
  );
}

/** A label over a value nobody edits here. */
export function VariantDetailValue({
  label,
  className,
  children,
}: {
  label: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <VariantDetailField label={label} className={className}>
      <span className="py-1 text-[13px] font-normal tabular-nums">{children}</span>
    </VariantDetailField>
  );
}

/** A switch with its name beside it. */
export function VariantDetailSwitch({
  label,
  className,
  children,
}: {
  label: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2 py-1 text-xs font-medium', className)}>
      {children}
      <span>{label}</span>
    </div>
  );
}

/** A variant's read-only stock in a row: a dash when it is not counted, zero stands out. */
export function StockFigure({ value }: { value: number | null | 'loading' }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  if (value === 'loading') return <span className="text-muted-foreground">…</span>;
  return (
    <span className={cn('tabular-nums', value <= 0 && 'text-warning font-medium')}>{value}</span>
  );
}
